import { useEffect, useState } from 'react';
import {
  generateGetAnswerValueByApiName,
  getPlanPaymentFrequency,
  getSelectedPlan,
} from '../../../store/policystreamStore';
import { styled } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Checkbox from '@mui/material/Checkbox';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { graphql } from '../../../gql';
import { GetPolicystreamAdvisorQuery } from '../../../gql/graphql';
import { useLazyQuery } from '@apollo/client';
import { getRecommendation } from '../../../store/policystreamStore';
import { POLICYSTREAM_API_NAMES } from '../constants';
import { formatAsReadableList } from '../../common/QuoteComponents/utility';
import { PolicyStreamLifeInsuranceNotRecommended } from '../components/PolicyStreamLifeInsuranceNotRecommended';
import { CoreQuotePolicystreamPlanPaymentFrequencyChoices as PolicyStreamPlanPaymentFrequencyChoices } from '../../../gql/graphql';
import { useSelector } from 'react-redux';
import { formatAsDollar } from '../utility';

interface ReviewDisclaimersDisplayProps {
  acceptsTermsAndConditions: boolean;
  setAcceptsTermsAndConditions: (accepts: boolean) => void;
}
export const ReviewDisclaimersDisplay = ({
  acceptsTermsAndConditions,
  setAcceptsTermsAndConditions,
}: ReviewDisclaimersDisplayProps) => {
  const selectedPlan = useSelector(getSelectedPlan);
  const recommendation = useSelector(getRecommendation);
  const purposeOfInsurance = useSelector(
    generateGetAnswerValueByApiName(
      POLICYSTREAM_API_NAMES.applicationGeneral.purposeOfInsurance,
      { internalName: 'application_general' },
    ),
  );
  const insuranceSubtype = useSelector(
    generateGetAnswerValueByApiName(
      POLICYSTREAM_API_NAMES.applicationGeneral.personalInsuranceSubtype,
      { internalName: 'application_general' },
    ),
  );
  const [open, setOpen] = useState<boolean>(false);
  const [advisor, setAdvisor] =
    useState<
      NonNullable<GetPolicystreamAdvisorQuery['getPolicystreamAdvisor']>
    >();
  const [premiumDescription, setPremiumDescription] = useState<string>('');
  const [coverageDescription, setCoverageDescription] = useState<string>('');
  const [getAdvisor, { data }] = useLazyQuery(GET_POLICYSTREAM_ADVISOR);
  const planPaymentFrequency = useSelector(getPlanPaymentFrequency);

  // get the advisor when we load
  useEffect(() => {
    getAdvisor();
  }, []);

  // set the data if it looks good
  useEffect(() => {
    if (data?.getPolicystreamAdvisor) {
      setAdvisor(data.getPolicystreamAdvisor);
    }
  }, [data]);

  // set the description of the coverage and premium amounts once we have it
  useEffect(() => {
    if (selectedPlan) {
      // premium section
      const monthlyAsNumber = Number(selectedPlan.premiumMonthlyDollars);
      const annualAsNumber = Number(selectedPlan.premiumAnnualDollars);
      const monthlyDescription =
        !Number.isNaN(monthlyAsNumber) && Number.isFinite(monthlyAsNumber)
          ? `${formatAsDollar(monthlyAsNumber, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} Monthly`
          : null;
      const annualDescription =
        !Number.isNaN(annualAsNumber) && Number.isFinite(annualAsNumber)
          ? `${formatAsDollar(annualAsNumber, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} Annually`
          : null;
      let newPremiumDescription: string = '';
      if (
        planPaymentFrequency ===
          PolicyStreamPlanPaymentFrequencyChoices.Annually &&
        annualDescription
      ) {
        newPremiumDescription = annualDescription;
      } else if (
        planPaymentFrequency ===
          PolicyStreamPlanPaymentFrequencyChoices.Monthly &&
        monthlyDescription
      ) {
        newPremiumDescription = monthlyDescription;
      }
      // const newPremiumDescription = [monthlyDescription, annualDescription]
      //   .filter(isNotNullish)
      //   .join(' / ');
      setPremiumDescription(newPremiumDescription);

      // coverage description
      const coverageAsNumber = Number(selectedPlan.coverageAmountDollars);
      const coverageDescription =
        !Number.isNaN(coverageAsNumber) && Number.isFinite(coverageAsNumber)
          ? `${formatAsDollar(coverageAsNumber)}`
          : '';
      setCoverageDescription(coverageDescription);
    } else {
      setPremiumDescription('');
      setCoverageDescription('');
    }
  }, []);

  if (!advisor) {
    return null;
  }
  return (
    <ContentsContainer>
      <Typography variant='h3'>Disclaimers</Typography>
      <section>
        <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
          Advisor Disclosure
        </Typography>
        {advisor ? (
          <div>
            <ul>
              {advisor?.name ? (
                <li>
                  <strong>Advisor Name:</strong> {advisor.name}
                </li>
              ) : null}
              {advisor?.jurisdiction ? (
                <li>
                  <strong>Jurisdiction:</strong> {advisor.jurisdiction}
                </li>
              ) : null}
              <li>
                <strong>Insurer(s) Represented:</strong>{' '}
                {advisor.insurers.length > 0
                  ? formatAsReadableList(
                      advisor.insurers.map((insurer) => insurer.displayName),
                    )
                  : 'None'}
              </li>
            </ul>

            <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
              Compensation Disclosure
            </Typography>
            <Typography sx={{ mb: 2 }}>
              I am compensated by the insurance company in the form of a
              commission when a policy is purchased. I may also receive renewal
              commissions for policies that remain in force. In some cases,
              additional bonuses, benefits, or incentives may be provided based
              on the overall business placed with an insurer.
            </Typography>

            <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
              Conflicts of Interest
            </Typography>
            <Typography sx={{ mb: 2 }}>
              If a potential conflict of interest arises during the course of
              our relationship, I will notify you promptly and provide any
              necessary details to ensure full transparency.
            </Typography>

            {advisor.policystreamMgas && advisor.policystreamMgas.length > 0 ? (
              <>
                <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
                  MGA Relationship
                </Typography>
                <Typography sx={{ mb: 1 }}>
                  <strong>MGA contracted through:</strong>{' '}
                  {formatAsReadableList(
                    advisor.policystreamMgas.map((mga) => mga.displayName),
                  )}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  While I operate as an independent advisor, my MGA may receive
                  compensation from insurers based on the overall business
                  volume placed.
                </Typography>
              </>
            ) : null}

            {advisor.securitiesDealerName ||
            advisor.mortgageDealerName ||
            advisor.designationType ? (
              <>
                <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
                  Additional Licenses
                </Typography>
                <Typography sx={{ mb: 1 }}>
                  I also hold the following licenses in addition to my life and
                  health insurance license:
                </Typography>
                <ul>
                  {advisor.securitiesDealerName ? (
                    <li>
                      <strong>
                        IIROC / MFDA - CIRO ({advisor.securitiesDealerName}):
                      </strong>{' '}
                      I am licensed to offer investment products through a
                      registered CIRO dealer. Life insurance recommendations are
                      made independently and are not provided or supervised by
                      the dealer.
                    </li>
                  ) : null}
                  {advisor.mortgageDealerName ? (
                    <li>
                      <strong>
                        Mortgage Broker / Agent ({advisor.mortgageDealerName}):
                      </strong>{' '}
                      Mortgage-related services are provided separately from
                      insurance services. Life, accident & sickness, and travel
                      insurance recommendations are made independently and are
                      not associated with the mortgage business.
                    </li>
                  ) : null}
                </ul>
                {advisor.designationType ? (
                  <Typography sx={{ mb: 2 }}>
                    I also hold the following professional designation(s):{' '}
                    <strong>{advisor.designationType}</strong>. These
                    designations reflect my training in holistic financial
                    planning. However, this engagement pertains only to life,
                    accident & sickness, and travel insurance services and is
                    not a financial planning engagement.
                  </Typography>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
      </section>
      <section>
        <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
          Needs Analysis and Recommendation
        </Typography>
        <section>
          <Typography variant='h5' sx={{ fontWeight: 'bold', pt: 2 }}>
            Identified Needs
          </Typography>
          {recommendation ? (
            recommendation.coverageAmountDollars &&
            recommendation.coverageAmountDollars <= 0 ? (
              <PolicyStreamLifeInsuranceNotRecommended />
            ) : (
              <ul>
                {purposeOfInsurance ? (
                  <li>
                    Financial Protection For:{' '}
                    {`${purposeOfInsurance}${
                      insuranceSubtype ? ` (${insuranceSubtype})` : ''
                    }`}
                  </li>
                ) : null}
                {recommendation.coverageAmountDollars ? (
                  <li>
                    Coverage Amount:{' '}
                    {formatAsDollar(recommendation.coverageAmountDollars)}
                  </li>
                ) : null}
                {recommendation.policyLengthYears ? (
                  <li>Term length: {recommendation.policyLengthYears} Years</li>
                ) : null}
              </ul>
            )
          ) : (
            <NoRecommendationComponent />
          )}
        </section>
        <section>
          <Typography variant='h5' sx={{ fontWeight: 'bold', pt: 2 }}>
            Chosen Product
          </Typography>
          {selectedPlan ? (
            <ul>
              <li>Product Type: Term Life Insurance</li>
              {selectedPlan?.insurer?.displayName ? (
                <li>Insurer: {selectedPlan.insurer.displayName} </li>
              ) : null}
              {coverageDescription ? (
                <li>Coverage Amount: {coverageDescription}</li>
              ) : null}
              {selectedPlan?.termLength ? (
                <li>Policy Term: {selectedPlan.termLength} Years</li>
              ) : null}
              {premiumDescription ? (
                <li>Premium: {premiumDescription}</li>
              ) : null}
            </ul>
          ) : (
            <Typography>You have not selected a plan.</Typography>
          )}
        </section>
      </section>
      <FormControlLabel
        label='I Accept the Terms and Conditions'
        control={
          <Checkbox
            checked={acceptsTermsAndConditions}
            onChange={(e) =>
              setAcceptsTermsAndConditions(e.currentTarget.checked)
            }
          />
        }
      />
      <Button onClick={() => setOpen(true)}>View Terms and Conditions</Button>
      <AdditionalDisclaimersDialog open={open} onOk={() => setOpen(false)} />
    </ContentsContainer>
  );
};

const NoRecommendationComponent = () => {
  return (
    <Typography>
      You chose not to complete a formal needs analysis. The coverage amount and
      term length were determined based on the preferences you provided. No
      comprehensive assessment of your financial or insurance needs was
      conducted.
    </Typography>
  );
};

interface AdditionalDisclaimersDialogProps {
  open: boolean;
  onOk: () => void;
}
const AdditionalDisclaimersDialog = ({
  open,
  onOk,
}: AdditionalDisclaimersDialogProps) => {
  return (
    <Dialog open={open}>
      <DialogTitle>Additional Disclaimers</DialogTitle>
      <DialogContent>
        <section>
          <Typography variant='h4'>Letter of Engagement</Typography>
          <Typography sx={{ mb: 2 }}>
            This Letter of Engagement outlines the agreement between you (the
            client) and your insurance advisor for life and health insurance
            services.
          </Typography>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Scope of Services
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Your advisor will include, but is not limited to:
          </Typography>
          <ul>
            <li>Presenting products from the insurers they represent;</li>
            <li>Assisting with the application process and policy issuance;</li>
            <li>Providing servicing or changes to your policy as required.</li>
          </ul>
          <Typography sx={{ mb: 2 }}>
            This may include life insurance, accident & sickness insurance, and
            travel insurance.
          </Typography>

          <Typography sx={{ mb: 1 }}>You agree to:</Typography>
          <ul>
            <li>Provide complete and accurate information;</li>
            <li>
              Notify your advisor of any changes that could impact your
              insurance;
            </li>
            <li>Review your policy documents promptly.</li>
          </ul>

          <Typography sx={{ mb: 2 }}>
            Details about the insurers represented, compensation, and potential
            conflicts of interest are provided in the Advisor Disclosure below.
          </Typography>

          <Typography sx={{ mb: 2 }}>
            This engagement pertains solely to life, accident & sickness, and
            travel insurance services. Any additional licenses or services I may
            hold, such as securities, investments, or mortgage brokering, are
            not included in the scope of this engagement and are subject to
            separate agreements and regulatory oversight.
          </Typography>
        </section>
        <section>
          <Typography variant='h4'>Privacy Policy and Consent</Typography>
          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Commitment to Protecting Your Privacy
          </Typography>
          <Typography sx={{ mb: 2 }}>
            To provide access to financial products and services, personal
            information must be collected. This Privacy Policy outlines how
            personal information is handled in compliance with applicable
            federal and provincial privacy laws, including the Personal
            Information Protection and Electronic Documents Act (PIPEDA).
          </Typography>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Accountability
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Advisors are responsible for the personal information they receive
            from clients and will take reasonable steps to safeguard that
            information in whatever form it is held.
          </Typography>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Why Personal Information is Collected
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Personal information (including financial, health, and related
            details) is collected, used, and retained solely for the purposes
            of:
          </Typography>
          <ul>
            <li>Providing advice</li>
            <li>
              Identifying financial products and services that meet identified
              needs
            </li>
            <li>Administering products or services purchased</li>
            <li>
              Notifying clients of new or updated products or services that may
              be of interest
            </li>
          </ul>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Consent
          </Typography>
          <Typography sx={{ mb: 1 }}>
            Personal information is collected only with consent and used for the
            purposes described above. By signing this form, you agree:
          </Typography>
          <ul>
            <li>
              To provide accurate information as your circumstances change
            </li>
            <li>
              To allow the use, sharing, and disclosure of your information on
              an as-needed basis with relevant suppliers, service providers, or
              organizations that support the delivery of financial products and
              services
            </li>
            <li>
              To allow retention of your information, including health
              information, in paper and electronic files for as long as is
              required for business or regulatory purposes
            </li>
            <li>
              To permit the assignment of your file, including personal
              information, to another licensed advisor or firm in the event of
              incapacity, retirement, or death, unless you choose otherwise
            </li>
          </ul>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Limiting Collection
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Only information necessary to provide advice, deliver services, and
            meet regulatory obligations will be collected, and only by fair and
            lawful means.
          </Typography>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Limits on Use, Disclosure, and Retention
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Personal information will be used and disclosed only to fulfill
            professional obligations, provide advice, and comply with legal
            requirements. This may include sharing information with service
            providers, insurers, or other parties where consent has been given
            or where permitted by law.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Personal information may be stored outside of the province or
            outside Canada by third parties that provide services, and may
            therefore be subject to the laws of those jurisdictions. Information
            will be retained only for as long as is required to meet business or
            regulatory obligations.
          </Typography>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Accuracy of Information
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Accurate information is essential to making appropriate
            recommendations. Efforts will be made to keep information as
            up-to-date as possible, and clients are responsible for informing
            the advisor of changes in their circumstances.
          </Typography>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Safeguards
          </Typography>
          <Typography sx={{ mb: 2 }}>
            All individuals and organizations granted access to personal
            information are required to maintain its confidentiality and use it
            only for authorized purposes. Physical, electronic, and procedural
            safeguards are in place to protect information from unauthorized
            access, use, or disclosure. Information that is no longer required
            will be securely destroyed.
          </Typography>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Your Privacy Choices
          </Typography>
          <Typography sx={{ mb: 2 }}>
            You may request access to the personal information retained about
            you at any time. Consent may also be withdrawn (subject to legal or
            contractual restrictions and with reasonable notice) by contacting
            the advisor. Withdrawing consent may limit the ability to provide
            products or services and could end the business relationship.
          </Typography>
          <Typography sx={{ mb: 1 }}>You may also:</Typography>
          <ul>
            <li>Request access to your personal information</li>
            <li>Request correction of inaccurate or incomplete information</li>
            <li>Request details about how your information is processed</li>
            <li>
              Request a copy of your personal information in a commonly used
              format
            </li>
          </ul>

          <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 2 }}>
            Complaints
          </Typography>
          <Typography sx={{ mb: 2 }}>
            If you have concerns about the collection, use, or disclosure of
            your personal information, you may contact the advisor, the
            designated privacy officer, or the Office of the Privacy
            Commissioner of Canada, or the appropriate provincial commissioner.
          </Typography>
        </section>
        <section>
          <Typography variant='h4'>Legal Disclaimers & Confirmation</Typography>
          This agreement is binding even though it is executed digitally. By
          signing electronically, you confirm that you agree to the terms and
          conditions of this document, authorize the use of your personal data
          for the purpose of processing your life insurance application, and
          consent to future correspondence related to your policy.
        </section>
        <section>
          <Typography variant='h4'>
            Anti-Money Laundering (AML) & Anti-Spam Compliance
          </Typography>
          <ul>
            <li>
              <strong>AML</strong>: In accordance with Canadian Anti-Money
              Laundering regulations, we may request additional information to
              verify your identity and confirm the source of funds used to
              purchase the policy.
            </li>{' '}
            <li>
              <strong>Anti-Spam Compliance</strong>: By signing this document,
              you consent to receive relevant communications, including policy
              updates, renewal notices, and other correspondence. You may opt
              out of non-essential communications at any time by contacting us.
            </li>{' '}
          </ul>
        </section>
        <section>
          <Typography variant='h4'>
            Acknowledgments and Client Consent
          </Typography>
          By signing below, you confirm that:
          <ol>
            <li>
              The information provided during this process is accurate and
              complete to the best of your knowledge.
            </li>
            <li>
              You understand and accept the product recommendation and the
              rationale provided.
            </li>{' '}
            <li>
              You consent to the sharing of your personal information with the
              insurer for the purposes of processing your application.
            </li>{' '}
            <li>
              You consent to receive all communications related to your
              application, policy, renewals, and statements electronically
              unless you opt-out in writing.
            </li>{' '}
            <li>
              You acknowledge that the premium quoted is not guaranteed and is
              subject to underwriting with the insurer.
            </li>{' '}
            <li>
              You understand that the policy obligations are the sole
              responsibility of the issuing insurance company.
            </li>{' '}
            <li>
              Your information will be kept on file until you request, in
              writing, for it to be destroyed or returned to you. This will also
              terminate our advisor-client relationship.
            </li>{' '}
            <li>
              You understand this is a data-gathering tool used for initial
              recommendations and will be followed by the submission of an
              official application to the insurer, which will require your
              separate signature and acceptance.
            </li>
          </ol>
        </section>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onOk()}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

const ContentsContainer = styled('div')(({ theme }) => {
  return {
    padding: theme.spacing(1),
  };
});

const GET_POLICYSTREAM_ADVISOR = graphql(`
  query getPolicystreamAdvisor {
    getPolicystreamAdvisor {
      id
      name
      jurisdiction
      insurers {
        displayName
      }
      securitiesDealerName
      mortgageDealerName
      designationType
      policystreamMgas {
        displayName
      }
    }
  }
`);
